import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';

/**
 * REV-22155 — one tag as a pill: `Steuern ×`.
 *
 * Built on `ham-filter-pill` (same shape, font and colours), which cannot be used as is: it has no
 * "×" and no dashed look for a tag that is not chosen.
 *
 * - `selected`: solid pill; otherwise dashed — the tag exists but is not chosen. The CSS class is
 *   `is-selected`, NOT `selected`: the overviews highlight a row with `tr:has(.selected)`, meant for
 *   the ticked checkbox, and a plain `selected` here would light up every row that carries a tag.
 * - `removable`: shows the "×" (delete the tag); a display-only pill (overview column) has none
 * - `highlighted`: filled — the tag whose deletion is being asked about
 *
 * Controlled: it only reports clicks, the host decides what happens. The label arrives ready to
 * show; nothing is translated here.
 */
@Component({
    selector: 'ham-tag-pill',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `<span
        class="ham-tag-pill"
        [class.is-selected]="selected"
        [class.highlighted]="highlighted"
        [class.clickable]="clickable"
    >
        <button
            *ngIf="clickable; else plainLabel"
            type="button"
            class="ham-tag-pill__label"
            [attr.aria-pressed]="selected"
            (click)="pillClick.emit()"
        >
            {{ label }}
        </button>
        <ng-template #plainLabel>
            <span class="ham-tag-pill__label">{{ label }}</span>
        </ng-template>
        <button
            *ngIf="removable"
            type="button"
            class="ham-tag-pill__remove"
            data-testid="tag-pill-remove"
            [attr.aria-label]="removeAriaLabel"
            (click)="removeClick.emit()"
        >
            <lucide-icon name="x" [size]="12" aria-hidden="true"></lucide-icon>
        </button>
    </span>`,
    styleUrls: ['./ham-tag-pill.component.scss'],
})
export class HamTagPillComponent {
    @Input() label = '';
    @Input() selected = true;
    @Input() removable = false;
    @Input() highlighted = false;
    /** Whether a click on the label means something (choose / unchoose); display-only pills are not. */
    @Input() clickable = false;
    /** Translated by the host, e.g. "Tag löschen". */
    @Input() removeAriaLabel = '';
    @Output() pillClick = new EventEmitter<void>();
    @Output() removeClick = new EventEmitter<void>();

    /** On the host, so a test finds the state where it finds the pill. */
    @HostBinding('attr.data-selected') get dataSelected(): string {
        return String(this.selected);
    }
}
